/**
 * Hydra Composite Node Definitions
 *
 * Two-input blend operations: add, mult, blend, diff, layer, mask
 */

import { HydraCompositeNode } from './HydraCompositeNode';
import type { NodeDef } from '@runtime/IRuntime';
import type { HydraConfig } from './modulates';
import { internals, params } from '@/nodes/TypedEndpoints';
import { Dependencies } from '@/nodes/Dependencies';

const hydraCompositeInOut = internals({
  hydraIn1: { dataType: 'hydra', isInput: true, label: 'A' },
  hydraIn2: { dataType: 'hydra', isInput: true, label: 'B' },
  hydraOut: { dataType: 'hydra', isOutput: true }
});

const compositeConfigs: HydraConfig[] = [
  {
    type: 'Add',
    shortName: 'add',
    hydraFn: 'add',
    params: {
      amount: { value: 0.5, min: 0, max: 1, step: 0.01 }
    }
  },
  {
    type: 'Mult',
    shortName: 'mult',
    hydraFn: 'mult',
    params: {
      amount: { value: 1, min: 0, max: 1, step: 0.01 }
    }
  },
  {
    type: 'Blend',
    shortName: 'blend',
    hydraFn: 'blend',
    params: {
      amount: { value: 0.5, min: 0, max: 1, step: 0.01 }
    }
  },
  {
    type: 'Diff',
    shortName: 'diff',
    hydraFn: 'diff',
    params: {}
  },
  {
    type: 'Layer',
    shortName: 'layer',
    hydraFn: 'layer',
    params: {}
  },
  {
    type: 'Mask',
    shortName: 'mask',
    hydraFn: 'mask',
    params: {
      reps: { value: 3, min: 1, max: 50, step: 1 },
      offset: { value: 0.5, min: 0, max: 20, step: 0.1 }
    }
  }
];

// Generate node definitions from configs
export const compositeNodes: NodeDef[] = compositeConfigs.map(config => {
  const endpoints = {
    ...params(config.params),
    ...hydraCompositeInOut
  };

  return {
    type: config.type,
    shortName: config.shortName,
    category: 'composite',
    endpoints,
    ctor: class extends HydraCompositeNode<typeof endpoints> {
      constructor(id: string, deps:Dependencies) {
        super(id, deps, {
          hydraFn: config.hydraFn,
          fnArgs: Object.keys(config.params)
        });
      }
    }
  };
});
